// Definições básicas para logs e depuração
const DEBUG_ENABLED = true;
const LOG_PREFIX = "[Mermaid Debug]";

// Função para gerar logs de diagnóstico
export function mermaidDebugLog(message: string, data?: any) {
  // Função não realiza nenhuma ação em ambiente de produção
  if (!DEBUG_ENABLED) return;

  // Os logs foram removidos conforme solicitado
}

// Função para detectar problemas comuns em código Mermaid
export function analyzeMermaidSyntax(code: string): string[] {
  const issues: string[] = [];

  // Verifica o tipo de diagrama
  const diagramType = detectDiagramType(code);

  if (!diagramType) {
    issues.push(
      "Não foi possível detectar o tipo de diagrama. Verifique a sintaxe inicial."
    );
    return issues;
  }

  // Verificações específicas para cada tipo de diagrama
  switch (diagramType) {
    case "graph":
    case "flowchart": {
      if (
        !code.includes("-->") &&
        !code.includes("---") &&
        !code.includes("--")
      ) {
        issues.push(
          "Nenhuma conexão (-->, ---, --) encontrada no diagrama de fluxo."
        );
      }

      // Verificar definições de nós sem IDs
      const invalidNodes = code.match(/\[[^\]]+\][^\s]/g);
      if (invalidNodes && invalidNodes.length > 0) {
        issues.push(
          "Possíveis nós mal formatados detectados. Certifique-se de que há espaço após cada definição de nó."
        );
      }
      break;
    }

    case "sequenceDiagram": {
      if (!code.includes("->")) {
        issues.push(
          "Nenhuma mensagem (->) encontrada no diagrama de sequência."
        );
      }

      if (!code.match(/participant\s+[A-Za-z0-9_]+/)) {
        issues.push(
          "Nenhum participante definido explicitamente. Considere definir participantes."
        );
      }
      break;
    }

    case "classDiagram": {
      if (!code.includes("class ") && !code.match(/\w+\s*[<|>-]+\s*\w+/)) {
        issues.push(
          "Nenhuma classe ou relação definida no diagrama de classe."
        );
      }
      break;
    }

    case "erDiagram": {
      if (
        !code.includes("||") &&
        !code.includes("|{") &&
        !code.includes("}|")
      ) {
        issues.push("Nenhuma relação (||, |{, }|) encontrada no diagrama ER.");
      }
      break;
    }
  }

  // Verificações comuns para todos os tipos
  // Chaves não balanceadas
  if ((code.match(/\{/g) || []).length !== (code.match(/\}/g) || []).length) {
    issues.push("Chaves { } não balanceadas no diagrama.");
  }

  // Parênteses não balanceados
  if ((code.match(/\(/g) || []).length !== (code.match(/\)/g) || []).length) {
    issues.push("Parênteses ( ) não balanceados no diagrama.");
  }

  // Colchetes não balanceados
  if ((code.match(/\[/g) || []).length !== (code.match(/\]/g) || []).length) {
    issues.push("Colchetes [ ] não balanceados no diagrama.");
  }

  return issues;
}

// Função para detectar o tipo de diagrama
export function detectDiagramType(code: string): string | null {
  const trimmedCode = code.trim();

  if (trimmedCode.startsWith("graph ")) {
    return "graph";
  } else if (trimmedCode.startsWith("flowchart ")) {
    return "flowchart";
  } else if (trimmedCode.startsWith("sequenceDiagram")) {
    return "sequenceDiagram";
  } else if (trimmedCode.startsWith("classDiagram")) {
    return "classDiagram";
  } else if (trimmedCode.startsWith("erDiagram")) {
    return "erDiagram";
  } else if (trimmedCode.startsWith("gantt")) {
    return "gantt";
  } else if (trimmedCode.startsWith("pie")) {
    return "pie";
  } else if (trimmedCode.startsWith("stateDiagram")) {
    return "stateDiagram";
  }

  return null;
}

// Função para tentar corrigir problemas comuns em diagramas
export function attemptToFixMermaidCode(code: string): string {
  const diagramType = detectDiagramType(code);
  let fixedCode = code.trim();

  if (!diagramType) {
    // Se não detectar o tipo, tenta inferir pelo conteúdo
    if (code.includes("-->") || code.includes("---")) {
      fixedCode = `graph TD\n${fixedCode}`;
    } else if (code.includes("->")) {
      fixedCode = `sequenceDiagram\n${fixedCode}`;
    } else {
      // Padrão para graph TD se não conseguir determinar
      fixedCode = `graph TD\n${fixedCode}`;
    }
    return fixedCode;
  }

  // Correções específicas por tipo
  switch (diagramType) {
    case "graph":
    case "flowchart":
      // Adiciona orientação se faltando
      if (
        !/graph\s+[A-Z]{2}/.test(fixedCode) &&
        !/flowchart\s+[A-Z]{2}/.test(fixedCode)
      ) {
        fixedCode = fixedCode.replace(/^(graph|flowchart)/, "$1 TD");
      }

      // Corrige nós sem espaçamento
      fixedCode = fixedCode.replace(/(\[[^\]]+\])([^\s])/g, "$1 $2");

      // Corrige setas mal formatadas (adiciona espaços)
      fixedCode = fixedCode.replace(
        /([A-Za-z0-9_\]"])--+>([A-Za-z0-9_\["]\b)/g,
        "$1 --> $2"
      );
      fixedCode = fixedCode.replace(
        /([A-Za-z0-9_\]"])--+([A-Za-z0-9_\["]\b)/g,
        "$1 -- $2"
      );

      // Corrige chaves não fechadas
      const openBrackets = (fixedCode.match(/\[/g) || []).length;
      const closeBrackets = (fixedCode.match(/\]/g) || []).length;
      if (openBrackets > closeBrackets) {
        fixedCode = fixedCode + "]".repeat(openBrackets - closeBrackets);
      }

      // Remove quebras de linha desnecessárias
      fixedCode = fixedCode.replace(/\n\s*\n/g, "\n");
      break;

    case "sequenceDiagram":
      // Certifica-se de que o diagrama começa corretamente
      if (!fixedCode.startsWith("sequenceDiagram")) {
        fixedCode = `sequenceDiagram\n${fixedCode}`;
      }

      // Corrige setas mal formatadas
      fixedCode = fixedCode.replace(/->>/g, "->");
      fixedCode = fixedCode.replace(/->/g, " -> ");

      // Adiciona participantes implícitos
      const implicitParticipants = new Set<string>();
      const messageRegex = /([A-Za-z0-9_]+)\s*-+>\s*([A-Za-z0-9_]+)/g;
      let match;
      while ((match = messageRegex.exec(fixedCode)) !== null) {
        implicitParticipants.add(match[1]);
        implicitParticipants.add(match[2]);
      }

      // Se não tiver definição explícita de participantes, adiciona
      if (!fixedCode.includes("participant ")) {
        const participantsStr = Array.from(implicitParticipants)
          .map((p) => `participant ${p}`)
          .join("\n");
        if (participantsStr) {
          fixedCode = `sequenceDiagram\n${participantsStr}\n${fixedCode.replace(
            "sequenceDiagram\n",
            ""
          )}`;
        }
      }
      break;
  }

  return fixedCode;
}

// Função para sanitizar o código Mermaid e remover caracteres problemáticos
export function sanitizeMermaidCode(code: string): string {
  if (!code) return "";

  let sanitized = code.trim();

  // Remove caracteres Unicode invisíveis que podem causar erros
  sanitized = sanitized.replace(/[\u200B-\u200D\uFEFF]/g, "");

  // Garante que há quebra de linha após a definição do tipo de diagrama
  const typeRegex =
    /^(graph\s+[A-Z]{2}|flowchart\s+[A-Z]{2}|sequenceDiagram|classDiagram|erDiagram|gantt|pie|stateDiagram)([^\n])/;
  sanitized = sanitized.replace(typeRegex, "$1\n$2");

  // Corrige aspas não fechadas
  const countSingleQuotes = (sanitized.match(/'/g) || []).length;
  if (countSingleQuotes % 2 !== 0) {
    sanitized = sanitized + "'";
  }

  const countDoubleQuotes = (sanitized.match(/"/g) || []).length;
  if (countDoubleQuotes % 2 !== 0) {
    sanitized = sanitized + '"';
  }

  return sanitized;
}

// Função para verificar compatibilidade de navegador para Mermaid
export function checkBrowserCompatibility(): string[] {
  const issues: string[] = [];

  // Verifica se estamos no navegador
  if (typeof window === "undefined") {
    return ["Ambiente não é um navegador"];
  }

  // Verifica SVG
  if (
    !document.implementation.hasFeature(
      "http://www.w3.org/TR/SVG11/feature#BasicStructure",
      "1.1"
    )
  ) {
    issues.push(
      "Navegador não suporta SVG adequadamente, o que é necessário para o Mermaid"
    );
  }

  // Verifica LocalStorage (usado por algumas configurações do Mermaid)
  try {
    localStorage.setItem("mermaid-test", "test");
    localStorage.removeItem("mermaid-test");
  } catch (e) {
    issues.push(
      "LocalStorage não está disponível, o que pode causar problemas com configurações do Mermaid"
    );
  }

  return issues;
}

export default {
  mermaidDebugLog,
  analyzeMermaidSyntax,
  detectDiagramType,
  attemptToFixMermaidCode,
  checkBrowserCompatibility,
};
